<script setup>
import _ from 'lodash'
import { ref, onMounted, computed, watch, getCurrentInstance, nextTick } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import IconBase from '../icon/IconBase.vue'
import IconFile from '../icon/IconFile.vue'
import IconGlobe from '../icon/IconGlobe.vue'
import IconCross from '../icon/IconCross.vue'
import IconSparkles from '../icon/IconSparkles.vue'
import FormButton from '../Atoms/FormButton.vue'
import { bytes } from '../../filtersNew'
import Tooltip from '../Atoms/Tooltip.vue';
import MetricsService from '../../services/MetricsService'
import UserService from "@/services/UserService"
import StreamService from '@/services/StreamService'
import FormSwitch from '../Atoms/FormSwitch.vue'
import FormInput from '../Atoms/FormInput.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import Alert from '../Atoms/Alert.vue'
import languages from '@/Utils/languages'
import { v4 } from "uuid"
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue';
import { track, EVENTS } from '@/lib/tracking';

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()

const captionLimitReachedMsg = 'Please contact sales, since your caption limit is exceeded'

const selectedLang = ref({ code: '', name: 'Select Language', nativeName: '' })

const languagesDropdown = ref(null)

const isLoading = ref(false)

const isGenerateCaption = ref(false)

const captions = ref({
  status: false,
  isProcessing: false,
  path: '',
  language: '',
  transcript: false,
  defaultCaption: null, // Stores the path of the default caption
});
const addonCaptions = ref([]);

const uploadInput = ref(null)
const languageFilter = ref('')

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  isLiveToVod: {
    type: Boolean,
    default: false,
  },
  playerUrl: {
    type: String,
    default: '',
  },
})

// Caption files of Live-to-VOD live under the live parent stream, keyed by the DVR archive id
const captionStreamId = computed(() => props.vodFolder.liveParent ?? route.params.streamId)
const captionVideoId = computed(() => props.vodFolder.liveParent ? props.vodFile._id : route.params.videoId)

// Player caches Live-to-VOD data under the live stream key, so clear by live parent + its key
const clearPlayerCache = () => {
  if (props.vodFolder.liveParent) {
    StreamService.clearStreamMetadataCache(props.vodFolder.liveParent, props.vodFile.archive?.key);
    return;
  }
  const url = props.playerUrl;
  let key = undefined;
  if (url.includes('?')) {
    key = url.split('?')[1]
  }
  StreamService.clearStreamMetadataCache(route.params.streamId, key);
}

const emit = defineEmits({
  'video-removed': () => true
})

onMounted(async () => {
  getCaptions()
})

watch(() => `${route.params.streamId}/${route.params.videoId}`, async () => {
  getCaptions()
})

watch(() => route.isLiveToVod, async () => {
  getCaptions()
})

const getCaptions = async () => {
  //TODO Move this call in parent component to prevent double Meta call for components (Chaptes, Schedule, etc.)

  let vodMeta;
  if(props.isLiveToVod) {
    vodMeta = await StreamService.getStreamMetadata(
      route.params.streamId,
      props.vodFile._id
    )
    vodMeta = vodMeta?.videoMeta;
  } else {
    vodMeta = await StreamService.getStreamPlaylistVideoMetaData(route.params.videoId);
  }

  if (vodMeta?.captions) {
    captions.value = vodMeta?.captions
    // if (vodMeta.captions.language) {
    //  selectedLang.value = languages.find(x => x.code === vodMeta.captions.language)
    // }
    checkCaptionGenerationStatus();
  }

  if (vodMeta?.addonCaptions) {
    addonCaptions.value = vodMeta?.addonCaptions ?? [];
  }
}

const checkCaptionGenerationStatus = async () => {  
  if (!captions.value.isProcessing || captions.value.path) {
    updateCaption({ isProcessing: false });
    isGenerateCaption.value = false;
    return;
  }

  isGenerateCaption.value = true;
  try {
    let videoId = props.vodFolder.liveParent ? props.vodFile._id : route.params.videoId;
    if (props.vodFile.replaceDetails && props.vodFile.replaceDetails.videoId) {
      videoId = props.vodFile.replaceDetails.videoId;
    }
    const result = await StreamService.checkFileExists(props.vodFolder.liveParent ?? route.params.streamId, videoId);
    if (result.exists) {
      notify({ group: 'success', text: 'Caption generated.' });
      track(EVENTS.CAPTIONS_ADDED, {
        source: 'ai_generated',
        folderId: props.vodFolder?._id,
        fileId: props.vodFile?._id,
      });
      updateCaption({ path: result.uri, isProcessing: false, language: '' });
      isGenerateCaption.value = false;
    }
    setTimeout(() => {
      checkCaptionGenerationStatus();
    }, 10000);
  } catch (err) {
    console.log(err);
  }
}

const onSelectLanguage = (lang) => {
  selectedLang.value = lang;
  languageFilter.value = ''
  languagesDropdown.value.toggleDropdown()
}

const canUploadFile = computed(() => {
  let totalCount = Number(!!(captions.value.path));
  totalCount += addonCaptions.value?.length ?? 0;
  const subs = store.state.User.subscriptions || [];
  const active = subs.filter(x => x.enabled && ['vod', 'live'].includes(x.category));
  let maxCount = 1;
  for(let sub of active) {
    if (sub.definitionOverride && !_.isNil(sub.definitionOverride.numOfSubtitles)) {
      maxCount = Number(sub.definitionOverride.numOfSubtitles);
      break;
    } else if (sub.package && !_.isNil(sub.package.definition.numOfSubtitles)) {
      maxCount = Math.max(Number(sub.package.definition.numOfSubtitles), maxCount);
    }
  }

  if(!active.length) {
    maxCount = 0;
  }

  return totalCount < maxCount;
})

const languagesMapByCode = computed(() => {
  let map = {};
  languages.forEach(x => {
    map[x.code] = x;
  });
  return map;
})

const languagesFiltered = computed(() => {
  return languages.filter(language => language.name.toLowerCase().includes(languageFilter.value.toLowerCase()))
})

const maxAiCaptionGenerationSizeReached = computed(() => (props.vodFile?.mediaInfo?.durationMins > 240) || (props.vodFile?.bytes / 1000000000) > 2)

// File size/duration blocks AI generation regardless of plan, so it takes priority over the caption limit
const aiTooltipText = computed(() => (captions.value.path && !captions.value.language) ? 'Only 1 AI Generation is allowed per video file.' : maxAiCaptionGenerationSizeReached.value
  ? 'AI Generation is currently unable to process files that exceed 2GB in size or have a duration longer than 4 hours. To ensure the inclusion of captions, kindly upload the corresponding subtitles.'
  : captionLimitReachedMsg
)

const uploadCounter = ref(0);

const onClickUploadCaptions = () => {
  uploadInput.value.click()
}
const startUpload = async () => {
  captions.value.isProcessing = true;
  console.log('starting upload');
  
  isLoading.value = true;
  const fdata = new FormData();
  fdata.append("file", uploadInput.value.files[0]);
  uploadCounter.value++;
  try {
    const uniqId = v4();
    const result = await StreamService.uploadVttFile(fdata, captionStreamId.value, captionVideoId.value, uniqId);
    updateCaption({ path: result.uri, isProcessing: false, language: selectedLang.value.code, });
    notify({ group: 'success', text: 'Caption file uploaded.' });
    track(EVENTS.CAPTIONS_ADDED, {
      captionsLanguage: selectedLang.value.code,
      source: 'upload',
      folderId: props.vodFolder?._id,
      fileId: props.vodFile?._id,
    });
    captions.value.isProcessing = false;
  } catch (err) {
    console.log(err);
    notify({ group: 'error', text: 'Failed to upload the caption file!' });
  }

  isLoading.value = false;
  selectedLang.value = { code: "", name: "Select Language", nativeName: "" };
}

const updateCaption = async (payload) => {
  if(!captions.value.path || 'transcript' in payload || (!payload.path && 'isProcessing' in payload) || (!payload.path && 'status' in payload)) {
    captions.value = {
      ...captions.value,
      ...payload,
    }
    if(props.isLiveToVod) {
      await StreamService.saveStreamMetadata(route.params.streamId, 'captions', captions.value, props.vodFile._id);
    } else {
      await StreamService.updateStreamPlaylistVideoMetaData(route.params.streamId, route.params.videoId, { captions: captions.value })
    }
  } else if(payload.path) {
    addonCaptions.value.push(payload);
    if(props.isLiveToVod) {
      await StreamService.saveStreamMetadata(route.params.streamId, 'addonCaptions', addonCaptions.value, props.vodFile._id);
    } else {
      await StreamService.updateStreamPlaylistVideoMetaData(route.params.streamId, route.params.videoId, { addonCaptions: addonCaptions.value })
    }
  }
  
  clearPlayerCache()
}

const removeCaption = async (fileName, isMain = false) => {
  console.log(fileName, isMain);
  
  if(isMain) {
    const removedPath = captions.value.path
    captions.value = {
      ...captions.value,
      isProcessing: false,
      path: '',
      language: '',
      defaultCaption: captions.value.defaultCaption === removedPath ? null : captions.value.defaultCaption,
    }
    if(props.isLiveToVod) {
      await StreamService.saveStreamMetadata(route.params.streamId, 'captions', captions.value, props.vodFile._id);
    } else {
      await StreamService.updateStreamPlaylistVideoMetaData(route.params.streamId, route.params.videoId, { captions: captions.value })
    }
  } else {
    addonCaptions.value = addonCaptions.value.filter(x => !x.path.includes(fileName));
    if(props.isLiveToVod) {
      await StreamService.saveStreamMetadata(route.params.streamId, 'addonCaptions', addonCaptions.value, props.vodFile._id);
    } else {
      await StreamService.updateStreamPlaylistVideoMetaData(route.params.streamId, route.params.videoId, { addonCaptions: addonCaptions.value })
    }
  }


  clearPlayerCache()
  console.log(captions.value);
  console.log(addonCaptions.value);
  
}

const onClickAiGenerateCaptions = async () => {
  if (captions.value.isProcessing || captions.value.path) {
    if(!canUploadFile.value) return;
    // move the existing main captions
    captions.value.isProcessing = false;
    updateCaption({...captions.value});
    captions.value.path = '';
    captions.value.language = '';
  }
  updateCaption({ isProcessing: true });
  isGenerateCaption.value = true;
  try {
    let videoId = props.vodFolder.liveParent ? props.vodFile._id : route.params.videoId;
    if (props.vodFile.replaceDetails && props.vodFile.replaceDetails.videoId) {
      videoId = props.vodFile.replaceDetails.videoId;
    }
    
    const result = await StreamService.generateVttAi(props.vodFolder.liveParent ?? route.params.streamId, videoId);
    if (result.success) {
      notify({ group: 'success', text: 'Caption generation started.' });
      checkCaptionGenerationStatus();
    } else {
      notify({ group: 'error', text: 'Unable to start caption generation!' });
      updateCaption({ isProcessing: false });
      isGenerateCaption.value = false
    }
  } catch (e) {
    console.log(e);
    notify({ group: 'error', text: 'Unable to start caption generation!' });
    updateCaption({ isProcessing: false });
  }
}
const formatFilePath = (str, caption = null, raw = false) => {
  if (!captions.value.language && !caption?.language && !raw) return 'Captions generated by AI';
  const x = str.split('/');
  if (!x.length) return '';
  return x[x.length - 1];
}
const captionFileName = (path) => {
  if (!path) return '';
  const parts = path.split('/');
  return parts[parts.length - 1] || '';
}
const deleteExistingCaptionFile = async (fileName = '', isMain = false) => {
  // this.featureProcessing.deleteCaptionFile = true;
  try {
    const result = await StreamService.deleteCaptionFile(captionStreamId.value, captionVideoId.value, fileName);
    if (result.success) {
      notify({ group: 'success', text: 'Caption file deleted.' });
      // updateCaption({ path: '', language: '' });
      removeCaption(fileName, isMain);
      selectedLang.value = { code: "", name: "Select Language", nativeName: "" };
    } else {
      notify({ group: 'error', text: 'Failed to delete caption!' });
    }
  } catch (err) {
    console.log(err);
    notify({ group: 'error', text: 'Failed to delete caption!' });
  }
  // this.featureProcessing.deleteCaptionFile = false;
}

const limitTextLength = (text) => {
  if(text.length > 15) {
    return text.slice(0, 6) + '...' + text.slice(text.length - 6);
  }
  return text;
}

const beforeCloseHandler = (args) => {
  // console.log(args.event.target.classList);
  // if (args.event && args.event.target && args.event.target.classList.contains("hover-tooltip")) {
  //   args.cancel = true;
  // } else {
  //   args.cancel = false;
  // }
}

const toggleDefaultCaption = async (captionPath) => {
  if (captions.value.defaultCaption === captionPath) {
    // Remove as default
    captions.value.defaultCaption = null;
  } else {
    // Set as default
    captions.value.defaultCaption = captionPath;
  }

  // Save to backend
  if(props.isLiveToVod) {
    await StreamService.saveStreamMetadata(route.params.streamId, 'captions', captions.value, props.vodFile._id);
  } else {
    await StreamService.updateStreamPlaylistVideoMetaData(route.params.streamId, route.params.videoId, { captions: captions.value })
  }

  clearPlayerCache()

  notify({ group: 'success', text: captions.value.defaultCaption ? 'Caption set as default!' : 'Default caption removed!' });
}

const isDefaultCaption = (captionPath) => {
  return captions.value.defaultCaption === captionPath;
}
</script>

<template>
  <section class="flex flex-col gap-4">
    <input :key="uploadCounter" class="!hidden" ref="uploadInput" @change="startUpload" type="file" :multiple="false" accept=".srt,.vtt">

    <!-- Card 1: Video caption -->
    <div class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4">
    <div class="flex items-center justify-between gap-3 !mb-3">
      <h3 class="text-base font-medium text-white">Video caption</h3>
      <FormSwitch label="Enable" labelPosition="left" data-funnel="enable"
        @change="updateCaption({ status: !captions.status, transcript: captions.status ? false : captions.status })" :modelValue="captions.status" />
    </div>

    <div class="flex gap-2 items-center flex-wrap !mb-4" >
      <DropDownMenu ref="languagesDropdown" type="secondary" :maxHeight="64" listWidth="w-full" autofocus-menu-input>
        <template #toggle-button>
          <icon-base class="!mr-2" fill="none">
            <icon-globe />
          </icon-base>
          {{ selectedLang.name }}
          <icon-base class="!ml-1">
            <icon-chevron-up-down />
          </icon-base>
        </template>
        <template #menu-items>
          <DropDownMenuItem isCustom class="!p-2 hover:!bg-transarent">
            <FormInput v-model="languageFilter" />
          </DropDownMenuItem>
          <DropDownMenuItem v-for="(lang, index) in languagesFiltered" :key="index" @click="onSelectLanguage(lang)">
            <span class="capitalize">{{ lang.name }} - {{ lang.nativeName }}</span>
          </DropDownMenuItem>
        </template>
      </DropDownMenu>
      <Tooltip :beforeClose="beforeCloseHandler" closeDelay='1000' v-if="!canUploadFile || maxAiCaptionGenerationSizeReached || (captions.path && !captions.language)" :content="aiTooltipText">
        <FormButton type="secondary" disabled>
          <icon-base class="mr-2 shrink-0" fill="transparent">
            <icon-sparkles />
          </icon-base>
          AI Caption Generator
        </FormButton>
      </Tooltip>
      <FormButton v-else type="secondary" @click="onClickAiGenerateCaptions"
        :isLoading="isGenerateCaption"
        :disabled="maxAiCaptionGenerationSizeReached || !canUploadFile || captions.isProcessing">
        <icon-base class="mr-2 shrink-0" fill="transparent">
          <icon-sparkles />
        </icon-base>
        AI Caption Generator
      </FormButton>
      <Tooltip v-if="!selectedLang.code" content="You need to select the language before uploading.">
        <FormButton type="main" disabled>
          <icon-base class="mr-2 shrink-0" fill="transparent">
            <icon-file />
          </icon-base>
          Upload SRT/VTT
        </FormButton>
      </Tooltip>
      <Tooltip :beforeClose="beforeCloseHandler" closeDelay='1000' v-else-if="!canUploadFile" :content="captionLimitReachedMsg">
        <FormButton type="main" disabled>
          <icon-base class="mr-2 shrink-0" fill="transparent">
            <icon-file />
          </icon-base>
          Upload SRT/VTT
        </FormButton>
      </Tooltip>
      <FormButton v-else type="main" @click="onClickUploadCaptions" :disabled="!canUploadFile" :isLoading="isLoading">
        <icon-base class="mr-2 shrink-0" fill="transparent">
          <icon-file />
        </icon-base>
        Upload SRT/VTT
      </FormButton>
    </div>

    <!-- Empty state -->
    <div v-if="!captions.path && !addonCaptions.length" class="py-12 text-center">
      <p class="text-white font-medium !mb-1">No captions uploaded</p>
      <p class="text-surface-8 text-sm">Upload SRT or VTT files to add captions</p>
    </div>

    <div class="my-3 flex gap-x-2 items-center !border border-surface-3 rounded-md text-surface-8"
      v-if="captions.path">

      <div class="!p-2 border-surface-3 border-right-only w-1/3 text-sm flex items-center gap-x-2">
        <!-- <img v-if="captions.language && languagesMapByCode[captions.language]" width="24" class="mr-2" :src="`https://assets.castr.io/countryflags/${languagesMapByCode[captions.language].code}/flat/64.png`"> -->
        {{ captions.language ? languagesMapByCode[captions.language].name : 'AI Generated' }}
      </div>


      <div class="!p-2 flex gap-x-2 items-center w-2/3 text-sm min-w-0">
        <icon-base class="mr-2 shrink-0" fill="transparent">
          <icon-file />
        </icon-base>
        <span class="truncate min-w-0">{{ limitTextLength(formatFilePath(captions.path)) }}</span>
        <svg class="shrink-0" v-if="isDefaultCaption(captions.path)" width="51" height="20" viewBox="0 0 51 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="51" height="20" rx="4" fill="#74DE85"/>
          <g filter="url(#filter0_d_12506_18458)">
            <path d="M7.78409 14H4.95881V5.27273H7.87358C8.72869 5.27273 9.46307 5.44744 10.0767 5.79688C10.6903 6.14347 11.1605 6.64205 11.4872 7.29261C11.8168 7.94034 11.9815 8.71733 11.9815 9.62358C11.9815 10.5327 11.8153 11.3139 11.483 11.9673C11.1534 12.6207 10.6761 13.1236 10.0511 13.4759C9.42614 13.8253 8.67045 14 7.78409 14ZM6.27557 12.8494H7.71165C8.37642 12.8494 8.92898 12.7244 9.36932 12.4744C9.80966 12.2216 10.1392 11.8565 10.358 11.3793C10.5767 10.8991 10.6861 10.3139 10.6861 9.62358C10.6861 8.93892 10.5767 8.35795 10.358 7.88068C10.142 7.40341 9.8196 7.04119 9.39062 6.79403C8.96165 6.54687 8.42898 6.4233 7.79261 6.4233H6.27557V12.8494ZM16.5777 14.1321C15.9328 14.1321 15.3774 13.9943 14.9115 13.7188C14.4485 13.4403 14.0905 13.0497 13.8377 12.5469C13.5877 12.0412 13.4627 11.4489 13.4627 10.7699C13.4627 10.0994 13.5877 9.50852 13.8377 8.99716C14.0905 8.4858 14.4428 8.08665 14.8945 7.79972C15.349 7.51278 15.8803 7.36932 16.4882 7.36932C16.8575 7.36932 17.2155 7.4304 17.5621 7.55256C17.9087 7.67472 18.2198 7.86648 18.4953 8.12784C18.7709 8.3892 18.9882 8.72869 19.1473 9.14631C19.3064 9.56108 19.386 10.0653 19.386 10.6591V11.1108H14.1828V10.1562H18.1374C18.1374 9.82102 18.0692 9.52415 17.9328 9.26562C17.7965 9.00426 17.6047 8.7983 17.3575 8.64773C17.1132 8.49716 16.8263 8.42188 16.4967 8.42188C16.1388 8.42188 15.8263 8.50994 15.5592 8.68608C15.295 8.85937 15.0905 9.08665 14.9456 9.3679C14.8036 9.64631 14.7325 9.94886 14.7325 10.2756V11.0213C14.7325 11.4588 14.8092 11.831 14.9627 12.1378C15.1189 12.4446 15.3362 12.679 15.6146 12.8409C15.8931 13 16.2183 13.0795 16.5905 13.0795C16.832 13.0795 17.0521 13.0455 17.251 12.9773C17.4499 12.9062 17.6217 12.8011 17.7666 12.6619C17.9115 12.5227 18.0223 12.3509 18.099 12.1463L19.305 12.3636C19.2084 12.7187 19.0351 13.0298 18.7851 13.2969C18.5379 13.5611 18.2269 13.767 17.8519 13.9148C17.4797 14.0597 17.055 14.1321 16.5777 14.1321ZM24.1547 7.45455V8.47727H20.4559V7.45455H24.1547ZM21.4701 14V6.69602C21.4701 6.28693 21.5595 5.94744 21.7385 5.67756C21.9175 5.40483 22.1547 5.2017 22.4502 5.06818C22.7456 4.93182 23.0666 4.86364 23.4132 4.86364C23.6689 4.86364 23.8877 4.88494 24.0695 4.92756C24.2513 4.96733 24.3862 5.00426 24.4743 5.03835L24.176 6.0696C24.1164 6.05256 24.0397 6.03267 23.9459 6.00994C23.8522 5.98437 23.7385 5.97159 23.605 5.97159C23.2953 5.97159 23.0737 6.0483 22.9402 6.2017C22.8095 6.35511 22.7442 6.5767 22.7442 6.86648V14H21.4701ZM27.415 14.1449C27.0002 14.1449 26.6252 14.0682 26.29 13.9148C25.9547 13.7585 25.6891 13.5327 25.4931 13.2372C25.2999 12.9418 25.2033 12.5795 25.2033 12.1506C25.2033 11.7812 25.2743 11.4773 25.4164 11.2386C25.5584 11 25.7502 10.8111 25.9917 10.6719C26.2331 10.5327 26.503 10.4276 26.8013 10.3565C27.0996 10.2855 27.4036 10.2315 27.7133 10.1946C28.1053 10.1491 28.4235 10.1122 28.6678 10.0838C28.9121 10.0526 29.0897 10.0028 29.2005 9.93466C29.3113 9.86648 29.3667 9.75568 29.3667 9.60227V9.57244C29.3667 9.20028 29.2615 8.91193 29.0513 8.70739C28.8439 8.50284 28.5343 8.40057 28.1223 8.40057C27.6934 8.40057 27.3553 8.49574 27.1081 8.68608C26.8638 8.87358 26.6948 9.08239 26.601 9.3125L25.4036 9.03977C25.5456 8.64205 25.753 8.32102 26.0258 8.0767C26.3013 7.82955 26.6181 7.65057 26.976 7.53977C27.334 7.42614 27.7104 7.36932 28.1053 7.36932C28.3667 7.36932 28.6436 7.40057 28.9363 7.46307C29.2317 7.52273 29.5073 7.63352 29.763 7.79545C30.0215 7.95739 30.2331 8.18892 30.3979 8.49006C30.5627 8.78835 30.6451 9.17614 30.6451 9.65341V14H29.4008V13.1051H29.3496C29.2672 13.2699 29.1436 13.4318 28.9789 13.5909C28.8141 13.75 28.6025 13.8821 28.3439 13.9872C28.0854 14.0923 27.7758 14.1449 27.415 14.1449ZM27.6919 13.1222C28.0442 13.1222 28.3454 13.0526 28.5954 12.9134C28.8482 12.7741 29.04 12.5923 29.1706 12.3679C29.3042 12.1406 29.3709 11.8977 29.3709 11.6392V10.7955C29.3255 10.8409 29.2374 10.8835 29.1067 10.9233C28.9789 10.9602 28.8326 10.9929 28.6678 11.0213C28.503 11.0469 28.3425 11.071 28.1863 11.0938C28.03 11.1136 27.8993 11.1307 27.7942 11.1449C27.5471 11.1761 27.3212 11.2287 27.1167 11.3026C26.915 11.3764 26.753 11.483 26.6309 11.6222C26.5115 11.7585 26.4519 11.9403 26.4519 12.1676C26.4519 12.483 26.5684 12.7216 26.8013 12.8835C27.0343 13.0426 27.3311 13.1222 27.6919 13.1222ZM36.7285 11.2855V7.45455H38.0069V14H36.754V12.8665H36.6859C36.5353 13.2159 36.2938 13.5071 35.9614 13.7401C35.6319 13.9702 35.2214 14.0852 34.7299 14.0852C34.3094 14.0852 33.9373 13.9929 33.6134 13.8082C33.2924 13.6207 33.0395 13.3438 32.8549 12.9773C32.6731 12.6108 32.5822 12.1577 32.5822 11.6179V7.45455H33.8563V11.4645C33.8563 11.9105 33.9799 12.2656 34.227 12.5298C34.4742 12.794 34.7952 12.9261 35.1901 12.9261C35.4287 12.9261 35.666 12.8665 35.9018 12.7472C36.1404 12.6278 36.3378 12.4474 36.4941 12.206C36.6532 11.9645 36.7313 11.6577 36.7285 11.2855ZM41.233 5.27273V14H39.9589V5.27273H41.233ZM46.2426 7.45455V8.47727H42.6673V7.45455H46.2426ZM43.6261 5.88636H44.9002V12.0781C44.9002 12.3253 44.9372 12.5114 45.011 12.6364C45.0849 12.7585 45.1801 12.8423 45.2965 12.8878C45.4159 12.9304 45.5451 12.9517 45.6843 12.9517C45.7866 12.9517 45.8761 12.9446 45.9528 12.9304C46.0295 12.9162 46.0891 12.9048 46.1318 12.8963L46.3619 13.9489C46.288 13.9773 46.1829 14.0057 46.0465 14.0341C45.9102 14.0653 45.7397 14.0824 45.5352 14.0852C45.1999 14.0909 44.8874 14.0312 44.5977 13.9062C44.3079 13.7812 44.0735 13.5881 43.8945 13.3267C43.7156 13.0653 43.6261 12.7372 43.6261 12.3423V5.88636Z" fill="#FAFAFB"/>
          </g>
          <defs>
            <filter id="filter0_d_12506_18458" x="3.95898" y="4.86365" width="43.4028" height="11.2812" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
              <feFlood flood-opacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset dy="1"/>
              <feGaussianBlur stdDeviation="0.5"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_12506_18458"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_12506_18458" result="shape"/>
            </filter>
          </defs>
        </svg>
        <FormButton class="ml-auto shrink-0" type="link" @click="toggleDefaultCaption(captions.path)">
          <span class="text-xs whitespace-nowrap">{{ isDefaultCaption(captions.path) ? 'Remove as default' : 'Set as default' }}</span>
        </FormButton>
        <FormButton class="shrink-0" type="link" @click="deleteExistingCaptionFile(captionFileName(captions.path), true)">
          <icon-base class="shrink-0" fill="transparent">
            <icon-cross />
          </icon-base>
        </FormButton>
      </div>
    </div>
    <div class="my-3 flex gap-x-2 items-center !border border-surface-3 rounded-md text-surface-8"
      v-for="addonCaption in addonCaptions" :key="addonCaption.path">

      <div class="!p-2 border-surface-3 border-right-only w-1/3 text-sm flex items-center gap-x-2">
        <!-- <img v-if="addonCaption.language && languagesMapByCode[addonCaption.language]" width="24" class="mr-2" :src="`https://assets.castr.io/countryflags/${languagesMapByCode[addonCaption.language].code}/flat/64.png`"> -->
        {{ addonCaption.language ? languagesMapByCode[addonCaption.language].name : 'AI Generated' }}
      </div>


      <div class="!p-2 flex gap-x-2 items-center w-2/3 text-sm min-w-0">
        <icon-base class="mr-2 shrink-0" fill="transparent">
          <icon-file />
        </icon-base>
        <span class="truncate min-w-0">{{ limitTextLength(formatFilePath(addonCaption.path, addonCaption)) }}</span>
        <svg v-if="isDefaultCaption(addonCaption.path)" width="51" height="20" viewBox="0 0 51 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="51" height="20" rx="4" fill="#74DE85"/>
          <g filter="url(#filter0_d_12506_18458_addon)">
            <path d="M7.78409 14H4.95881V5.27273H7.87358C8.72869 5.27273 9.46307 5.44744 10.0767 5.79688C10.6903 6.14347 11.1605 6.64205 11.4872 7.29261C11.8168 7.94034 11.9815 8.71733 11.9815 9.62358C11.9815 10.5327 11.8153 11.3139 11.483 11.9673C11.1534 12.6207 10.6761 13.1236 10.0511 13.4759C9.42614 13.8253 8.67045 14 7.78409 14ZM6.27557 12.8494H7.71165C8.37642 12.8494 8.92898 12.7244 9.36932 12.4744C9.80966 12.2216 10.1392 11.8565 10.358 11.3793C10.5767 10.8991 10.6861 10.3139 10.6861 9.62358C10.6861 8.93892 10.5767 8.35795 10.358 7.88068C10.142 7.40341 9.8196 7.04119 9.39062 6.79403C8.96165 6.54687 8.42898 6.4233 7.79261 6.4233H6.27557V12.8494ZM16.5777 14.1321C15.9328 14.1321 15.3774 13.9943 14.9115 13.7188C14.4485 13.4403 14.0905 13.0497 13.8377 12.5469C13.5877 12.0412 13.4627 11.4489 13.4627 10.7699C13.4627 10.0994 13.5877 9.50852 13.8377 8.99716C14.0905 8.4858 14.4428 8.08665 14.8945 7.79972C15.349 7.51278 15.8803 7.36932 16.4882 7.36932C16.8575 7.36932 17.2155 7.4304 17.5621 7.55256C17.9087 7.67472 18.2198 7.86648 18.4953 8.12784C18.7709 8.3892 18.9882 8.72869 19.1473 9.14631C19.3064 9.56108 19.386 10.0653 19.386 10.6591V11.1108H14.1828V10.1562H18.1374C18.1374 9.82102 18.0692 9.52415 17.9328 9.26562C17.7965 9.00426 17.6047 8.7983 17.3575 8.64773C17.1132 8.49716 16.8263 8.42188 16.4967 8.42188C16.1388 8.42188 15.8263 8.50994 15.5592 8.68608C15.295 8.85937 15.0905 9.08665 14.9456 9.3679C14.8036 9.64631 14.7325 9.94886 14.7325 10.2756V11.0213C14.7325 11.4588 14.8092 11.831 14.9627 12.1378C15.1189 12.4446 15.3362 12.679 15.6146 12.8409C15.8931 13 16.2183 13.0795 16.5905 13.0795C16.832 13.0795 17.0521 13.0455 17.251 12.9773C17.4499 12.9062 17.6217 12.8011 17.7666 12.6619C17.9115 12.5227 18.0223 12.3509 18.099 12.1463L19.305 12.3636C19.2084 12.7187 19.0351 13.0298 18.7851 13.2969C18.5379 13.5611 18.2269 13.767 17.8519 13.9148C17.4797 14.0597 17.055 14.1321 16.5777 14.1321ZM24.1547 7.45455V8.47727H20.4559V7.45455H24.1547ZM21.4701 14V6.69602C21.4701 6.28693 21.5595 5.94744 21.7385 5.67756C21.9175 5.40483 22.1547 5.2017 22.4502 5.06818C22.7456 4.93182 23.0666 4.86364 23.4132 4.86364C23.6689 4.86364 23.8877 4.88494 24.0695 4.92756C24.2513 4.96733 24.3862 5.00426 24.4743 5.03835L24.176 6.0696C24.1164 6.05256 24.0397 6.03267 23.9459 6.00994C23.8522 5.98437 23.7385 5.97159 23.605 5.97159C23.2953 5.97159 23.0737 6.0483 22.9402 6.2017C22.8095 6.35511 22.7442 6.5767 22.7442 6.86648V14H21.4701ZM27.415 14.1449C27.0002 14.1449 26.6252 14.0682 26.29 13.9148C25.9547 13.7585 25.6891 13.5327 25.4931 13.2372C25.2999 12.9418 25.2033 12.5795 25.2033 12.1506C25.2033 11.7812 25.2743 11.4773 25.4164 11.2386C25.5584 11 25.7502 10.8111 25.9917 10.6719C26.2331 10.5327 26.503 10.4276 26.8013 10.3565C27.0996 10.2855 27.4036 10.2315 27.7133 10.1946C28.1053 10.1491 28.4235 10.1122 28.6678 10.0838C28.9121 10.0526 29.0897 10.0028 29.2005 9.93466C29.3113 9.86648 29.3667 9.75568 29.3667 9.60227V9.57244C29.3667 9.20028 29.2615 8.91193 29.0513 8.70739C28.8439 8.50284 28.5343 8.40057 28.1223 8.40057C27.6934 8.40057 27.3553 8.49574 27.1081 8.68608C26.8638 8.87358 26.6948 9.08239 26.601 9.3125L25.4036 9.03977C25.5456 8.64205 25.753 8.32102 26.0258 8.0767C26.3013 7.82955 26.6181 7.65057 26.976 7.53977C27.334 7.42614 27.7104 7.36932 28.1053 7.36932C28.3667 7.36932 28.6436 7.40057 28.9363 7.46307C29.2317 7.52273 29.5073 7.63352 29.763 7.79545C30.0215 7.95739 30.2331 8.18892 30.3979 8.49006C30.5627 8.78835 30.6451 9.17614 30.6451 9.65341V14H29.4008V13.1051H29.3496C29.2672 13.2699 29.1436 13.4318 28.9789 13.5909C28.8141 13.75 28.6025 13.8821 28.3439 13.9872C28.0854 14.0923 27.7758 14.1449 27.415 14.1449ZM27.6919 13.1222C28.0442 13.1222 28.3454 13.0526 28.5954 12.9134C28.8482 12.7741 29.04 12.5923 29.1706 12.3679C29.3042 12.1406 29.3709 11.8977 29.3709 11.6392V10.7955C29.3255 10.8409 29.2374 10.8835 29.1067 10.9233C28.9789 10.9602 28.8326 10.9929 28.6678 11.0213C28.503 11.0469 28.3425 11.071 28.1863 11.0938C28.03 11.1136 27.8993 11.1307 27.7942 11.1449C27.5471 11.1761 27.3212 11.2287 27.1167 11.3026C26.915 11.3764 26.753 11.483 26.6309 11.6222C26.5115 11.7585 26.4519 11.9403 26.4519 12.1676C26.4519 12.483 26.5684 12.7216 26.8013 12.8835C27.0343 13.0426 27.3311 13.1222 27.6919 13.1222ZM36.7285 11.2855V7.45455H38.0069V14H36.754V12.8665H36.6859C36.5353 13.2159 36.2938 13.5071 35.9614 13.7401C35.6319 13.9702 35.2214 14.0852 34.7299 14.0852C34.3094 14.0852 33.9373 13.9929 33.6134 13.8082C33.2924 13.6207 33.0395 13.3438 32.8549 12.9773C32.6731 12.6108 32.5822 12.1577 32.5822 11.6179V7.45455H33.8563V11.4645C33.8563 11.9105 33.9799 12.2656 34.227 12.5298C34.4742 12.794 34.7952 12.9261 35.1901 12.9261C35.4287 12.9261 35.666 12.8665 35.9018 12.7472C36.1404 12.6278 36.3378 12.4474 36.4941 12.206C36.6532 11.9645 36.7313 11.6577 36.7285 11.2855ZM41.233 5.27273V14H39.9589V5.27273H41.233ZM46.2426 7.45455V8.47727H42.6673V7.45455H46.2426ZM43.6261 5.88636H44.9002V12.0781C44.9002 12.3253 44.9372 12.5114 45.011 12.6364C45.0849 12.7585 45.1801 12.8423 45.2965 12.8878C45.4159 12.9304 45.5451 12.9517 45.6843 12.9517C45.7866 12.9517 45.8761 12.9446 45.9528 12.9304C46.0295 12.9162 46.0891 12.9048 46.1318 12.8963L46.3619 13.9489C46.288 13.9773 46.1829 14.0057 46.0465 14.0341C45.9102 14.0653 45.7397 14.0824 45.5352 14.0852C45.1999 14.0909 44.8874 14.0312 44.5977 13.9062C44.3079 13.7812 44.0735 13.5881 43.8945 13.3267C43.7156 13.0653 43.6261 12.7372 43.6261 12.3423V5.88636Z" fill="#FAFAFB"/>
          </g>
          <defs>
            <filter id="filter0_d_12506_18458_addon" x="3.95898" y="4.86365" width="43.4028" height="11.2812" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
              <feFlood flood-opacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset dy="1"/>
              <feGaussianBlur stdDeviation="0.5"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_12506_18458_addon"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_12506_18458_addon" result="shape"/>
            </filter>
          </defs>
        </svg>
        <FormButton class="ml-auto shrink-0" type="link" @click="toggleDefaultCaption(addonCaption.path)">
          <span class="text-xs whitespace-nowrap">{{ isDefaultCaption(addonCaption.path) ? 'Remove as default' : 'Set as default' }}</span>
        </FormButton>
        <FormButton class="shrink-0" type="link" @click="deleteExistingCaptionFile(captionFileName(addonCaption.path))">
          <icon-base class="shrink-0" fill="transparent">
            <icon-cross />
          </icon-base>
        </FormButton>
      </div>
    </div>
    
    </div>
    <!-- End Card 1 -->

    <!-- Card 2: Interactive Transcript -->
    <div class="rounded-lg ring-1 ring-surface-3 bg-surface-2 p-4">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-base font-medium text-white">Interactive Transcript</h3>
          <div class="text-surface-8 text-sm mt-0.5">When enabled, viewers can see real-time transcripts alongside the video and click on any word to jump to that point in playback.</div>
        </div>
        <div class="shrink-0">
          <Tooltip :beforeClose="beforeCloseHandler" closeDelay='1000' v-if="!captions.status" content="Please enable captions first">
            <FormSwitch label="Enable" labelPosition="left" :disabled="!captions.status"
              @change="updateCaption({ transcript: !captions.transcript })" :modelValue="captions.status && captions.transcript" />
          </Tooltip>
          <FormSwitch v-else data-funnel="interactive" label="Enable" labelPosition="left" :disabled="!captions.status"
            @change="updateCaption({ transcript: !captions.transcript })" :modelValue="captions.status && captions.transcript" />
        </div>
      </div>
      <Alert class="!mt-3" v-if="!captions.status">
        <p>This feature requires Captions to be enabled first. Please turn on Captions before enabling Interactive Transcript.</p>
      </Alert>
    </div>
  </section>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.disabled-font-style {
  color: #8794B5;
  opacity: .30;
}
.border-right-only {
  border-right-width: 1px;
  border-right-style: solid;
}
</style>
